import {
  Box,
  Flex,
  Heading,
  IconButton,
  Progress,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Text,
  Tooltip,
  Tr,
  Button,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  Table as TanStackTable,
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  DeliverableContainerGQLType,
  useGetDeliverableContainersQuery,
  useGetUserQuery,
  useManuallyCreateDeliverableMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { calculateStatus, Status, StatusCell, statusColor } from '../Status';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { logOutOnAuthErrors } from 'packages/common-frontend/utils';
import {
  getFilterValue,
  ColumnFilter,
  customFullDateStrFilter,
  customMultiFilter,
} from 'packages/deliverables/ui/DeliverablesColumnFilter';
import { useAuthToken } from 'packages/logged-out/hooks/useAuthManagement';

const MAX_GET_STARTED_DAYS = 30; // Max number of days before a deliverable is due where a user can manually materialize a record.

const computedStatusSortOrder: { [key in Status]: number } = {
  overdue: 5,
  dueSoon: 4,
  notStarted: 3,
  generatingReport: 2,
  complete: 1,
};

const statusOptions = [
  { label: <StatusCell status="dueSoon" />, value: 'dueSoon' },
  { label: <StatusCell status="notStarted" />, value: 'notStarted' },
  { label: <StatusCell status="overdue" />, value: 'overdue' },
  { label: <StatusCell status="complete" />, value: 'complete' },
  {
    label: <StatusCell status="generatingReport" />,
    value: 'generatingReport',
  },
];

const getRowBackgroundColor = (deliverable: DeliverableContainerGQLType) => {
  const status = calculateStatus(deliverable);

  switch (status) {
    case 'overdue':
      return 'red.100';
    default:
      return 'none';
  }
};

const filterStatuses = (
  table: TanStackTable<DeliverableContainerGQLType>,
  status: string,
) => {
  const column = table.getColumn('calculatedStatus');
  if (column) {
    const currentFilters = getFilterValue(column);
    const index = currentFilters.indexOf(status);
    if (index >= 0) {
      currentFilters.splice(index, 1);
    } else {
      currentFilters.push(status);
    }
    column.setFilterValue(currentFilters);
  }
};

const DeliverableContainersTable: React.FC<{
  containers: DeliverableContainerGQLType[];
  refetchDeliverableContainers: Function;
}> = ({ containers, refetchDeliverableContainers }) => {
  const [
    manuallyCreateDeliverableMutation,
  ] = useManuallyCreateDeliverableMutation();

  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'calculatedStatus', desc: true },
    { id: 'dueDate', desc: false },
  ]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([
    { id: 'calculatedStatus', value: ['overdue', 'dueSoon'] },
  ]);
  const history = useHistory();

  const statusCounts = useMemo(() => {
    let overdueCount: number = 0;
    let dueSoonCount: number = 0;
    for (const container of containers) {
      const status = calculateStatus(container);
      if (status === 'dueSoon') {
        dueSoonCount += 1;
      }
      if (status === 'overdue') {
        overdueCount += 1;
      }
    }

    return { overdueCount, dueSoonCount };
  }, [containers]);

  // on refetch, containers doesn't update without useMemo
  const data = useMemo(() => containers, [containers]);

  const statusFilters = useMemo(() => {
    return _.flatMap(
      columnFilters
        .filter((cf) => cf.id === 'calculatedStatus')
        .map((cf) => cf.value),
    );
  }, [columnFilters]);

  const columns: ColumnDef<DeliverableContainerGQLType, any>[] = [
    {
      accessorKey: 'creditFacilitySlug',
      header: 'Credit Facility',
      enableSorting: false,
      cell: ({ getValue }) => commonUtils.toTitleCase(getValue()),
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Credit Facility/Facilities',
      },
    },
    {
      id: 'reportType',
      accessorKey: 'type',
      enableSorting: false,
      header: 'Report Type',
      cell: ({ getValue }) => commonUtils.toTitleCase(getValue()),
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Report Type(s)',
      },
    },
    {
      id: 'dueDate',
      accessorKey: 'dueDate',
      enableSorting: true,
      header: 'Due Date',
      cell: ({ getValue }) =>
        commonDate.fullDateStrToHumanDateString(getValue()),
      filterFn: customFullDateStrFilter,
      meta: {
        filterHeader: 'Select Due Date',
        filterType: 'date',
      },
    },
    {
      id: 'requiredFiles',
      enableSorting: false,
      enableColumnFilter: false,
      header: 'Required Files',
      size: 275,
      // eslint-disable-next-line react/display-name
      cell: ({ row }) => {
        const {
          totalDependencies,
          completedDependencies,
          dueDate,
          creditFacilityId,
          configKey,
          daysOffset,
        } = row.original;
        const progressValue = (completedDependencies / totalDependencies) * 100;
        const status = calculateStatus(row.original);
        if (status === 'notStarted') {
          const currentDate = commonDate.currentDateStr();
          const diff = commonDate.diffDays(dueDate, currentDate);
          if (diff <= MAX_GET_STARTED_DAYS) {
            return (
              <Text fontSize="xs">
                {`We will notify you ${daysOffset} before the deadline.`}{' '}
                <Button
                  color="blue.400"
                  fontSize="xs"
                  variant="link"
                  onClick={async () => {
                    const {
                      data: newDeliverableData,
                    } = await manuallyCreateDeliverableMutation({
                      variables: {
                        creditFacilityId,
                        dueDate,
                        configKey,
                      },
                    });
                    refetchDeliverableContainers();

                    if (
                      newDeliverableData?.manuallyCreateDeliverableByDueDate
                    ) {
                      const id =
                        newDeliverableData.manuallyCreateDeliverableByDueDate
                          .id;
                      history.push(`/deliverables/${id}`);
                    }
                  }}
                >
                  Get started
                </Button>{' '}
                now if you&apos;d like.
              </Text>
            );
          }
          return <Text>-</Text>;
        }
        return (
          <Stack>
            <Text>{`${completedDependencies}/${totalDependencies}`}</Text>
            <Progress
              value={progressValue}
              colorScheme="green"
              borderRadius={8}
              size="sm"
            />
          </Stack>
        );
      },
    },
    {
      id: 'calculatedStatus',
      accessorFn: (row) => calculateStatus(row),
      enableColumnFilter: true,
      header: 'Status',
      // eslint-disable-next-line react/display-name
      cell: ({ row }) => <StatusCell status={calculateStatus(row.original)} />,
      sortingFn: (rowA, rowB) => {
        const statusRowA = calculateStatus(rowA.original);
        const statusRowB = calculateStatus(rowB.original);
        if (
          computedStatusSortOrder[statusRowA] >
          computedStatusSortOrder[statusRowB]
        ) {
          return 1;
        }
        if (
          computedStatusSortOrder[statusRowA] <
          computedStatusSortOrder[statusRowB]
        ) {
          return -1;
        }
        return 0;
      },
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Status(es)',
        filterOptions: statusOptions,
      },
    },
    {
      id: 'moreDetail',
      enableSorting: false,
      enableColumnFilter: false,
      // eslint-disable-next-line react/display-name
      cell: ({ row }) => {
        const { id } = row.original;
        const status = calculateStatus(row.original);
        if (status === 'notStarted') {
          return <></>;
        }
        return (
          <Tooltip bg="gray.500" hasArrow label="More details">
            <IconButton
              aria-label="more details"
              icon={<Icon icon="chevron-right" />}
              variant="ghost"
              onClick={() => history.push(`/deliverables/${id}`)}
            />
          </Tooltip>
        );
      },
      meta: {
        style: {
          padding: '0px',
          textAlign: 'right',
        },
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    enableMultiSort: true,
    state: {
      sorting,
      columnFilters,
    },
    initialState: {
      sorting,
      columnFilters,
      pagination: {
        pageIndex: 0,
        pageSize: 35,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <Box>
      <SimpleGrid columns={2} spacing={10} mb={8}>
        <Button
          leftIcon={
            <Icon icon="calendar-clock" color={`${statusColor.overdue}.300`} />
          }
          justifyContent="flex-start"
          py={6}
          variant="outline"
          bgColor={statusFilters.includes('overdue') ? 'gray.100' : 'inherit'}
          onClick={() => {
            filterStatuses(table, 'overdue');
          }}
        >
          {`Overdue ${statusCounts.overdueCount}`}
        </Button>
        <Button
          leftIcon={
            <Icon icon="calendar-clock" color={`${statusColor.dueSoon}.300`} />
          }
          justifyContent="flex-start"
          py={6}
          variant="outline"
          bgColor={statusFilters.includes('dueSoon') ? 'gray.100' : 'inherit'}
          onClick={() => {
            filterStatuses(table, 'dueSoon');
          }}
        >
          {`Due Soon ${statusCounts.dueSoonCount}`}
        </Button>
      </SimpleGrid>
      <Table borderWidth="1px" borderColor={colors.gray[100]}>
        <Thead>
          {_.map(table.getHeaderGroups(), (headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const columnCanSort = header.column.getCanSort();
                const columnCanFilter = header.column.getCanFilter();

                return (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    textTransform="capitalize"
                    padding={
                      header.column.columnDef.meta?.style?.padding ||
                      '8px 16px 8px 16px'
                    }
                    fontSize="16px"
                    fontWeight="medium"
                    whiteSpace="nowrap"
                    color={colors.black[95]}
                    width={
                      header.id === 'requiredFiles' ? header.getSize() : 'auto'
                    }
                  >
                    <Flex alignItems="center">
                      <Box
                        cursor={columnCanSort ? 'pointer' : 'default'}
                        onClick={header.column.getToggleSortingHandler()}
                        mr="8px"
                        color={colors.aurora[400]}
                        fontWeight="thin"
                      >
                        {{
                          asc: <Icon icon="sort-up" />,
                          desc: <Icon icon="sort-down" />,
                        }[header.column.getIsSorted() as string] ??
                          (columnCanSort ? <Icon icon="sort" /> : null)}
                      </Box>
                      <Box>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Box>
                      {columnCanFilter && (
                        <ColumnFilter
                          key={JSON.stringify(getFilterValue(header.column))}
                          type={header.column.columnDef.meta?.filterType}
                          header={header.column.columnDef.meta?.filterHeader}
                          options={header.column.columnDef.meta?.filterOptions}
                          column={header.column}
                          valueFormatFn={
                            header.column.columnDef.meta?.filterFormatFn
                          }
                        />
                      )}
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody color="gray.600" fontSize="sm">
          {table.getRowModel().rows.map((row) => (
            <Tr
              key={row.id}
              backgroundColor={getRowBackgroundColor(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  textAlign={cell.column.columnDef.meta?.style?.textAlign}
                  padding={cell.column.columnDef.meta?.style?.padding}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export const DeliverableContainers: React.FC<{
  creditFacilityIds: string[];
}> = ({ creditFacilityIds }) => {
  const [, , removeAuthToken] = useAuthToken();
  const history = useHistory();

  const {
    loading: deliverableContainersLoading,
    data: deliverableContainersData,
    refetch: refetchDeliverableContainers,
  } = useGetDeliverableContainersQuery({
    fetchPolicy: 'no-cache',
    variables: {
      creditFacilityIds,
      limit: 100,
    },
    onError: ({ graphQLErrors }) => {
      logOutOnAuthErrors(graphQLErrors, removeAuthToken, history);
    },
  });

  const { data: userData, loading: userLoading } = useGetUserQuery();

  if (
    !deliverableContainersData ||
    !deliverableContainersData.getDeliverableContainers ||
    deliverableContainersLoading ||
    !userData ||
    !userData.user ||
    userLoading
  ) {
    return (
      <Skeleton data-testid="loading-deliverables-v4-view" height="600px" />
    );
  }

  const deliverableContainers: DeliverableContainerGQLType[] =
    deliverableContainersData.getDeliverableContainers;

  return (
    <Box height="100%">
      <Flex justify="space-between">
        <Heading as="h2" fontSize="3xl" pb="16px">
          Deliverables
        </Heading>
      </Flex>
      {deliverableContainers.length === 0 ? (
        <Text>No deliverables are currently configured.</Text>
      ) : (
        <DeliverableContainersTable
          containers={deliverableContainers}
          refetchDeliverableContainers={refetchDeliverableContainers}
        />
      )}
    </Box>
  );
};
